import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import type { CustomEmberTableRow } from '@mylighthouse/prism-ember/types/components/prism/table';
import TransactionCellsTdButton from 'frontend-payments/components/transaction/cells/td-button';
import type { Transaction } from 'frontend-payments/models';
import { TRANSACTION_TYPE_TRANSLATION_KEYS } from 'frontend-payments/models/transaction';
import { getHostDomain } from 'frontend-payments/utils/domain-checks';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import { openUrl } from 'frontend-utils/utils/url/open-url';

import styles from './table-pay-balance-invoices.module.css';

const COLUMNS = {
  INVOICE_NUMBER: 'invoiceNumber',
  INVOICE_DATE: 'invoiceDate',
  INVOICE_DATE_FORMATTED: 'invoiceDateFormatted',
  DUE_DATE: 'dueDate',
  DUE_DATE_FORMATTED: 'dueDateFormatted',
  INVOICE_TOTAL: 'invoiceTotal',
  INVOICE: 'invoice',
} as const;

interface TransactionTableInvoicesSignature {
  Args: {
    isTransactionRequestLoading: boolean;
    rowSelection: CustomEmberTableRow[];
    setRowSelection: (
      selection: CustomEmberTableRow | CustomEmberTableRow[],
    ) => void;
    transactions: Transaction[];
  };
}

export default class TransactionTableInvoices extends Component<TransactionTableInvoicesSignature> {
  @service declare intl: Services['intl'];

  initialSorts = [
    {
      valuePath: COLUMNS.INVOICE_DATE,
      isAscending: false,
    },
  ];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    return [
      {
        name: this.intl.t('payments.invoice.table.header.invoice_number'),
        valuePath: COLUMNS.INVOICE_NUMBER,
        textAlign: 'left' as const,
        width: 140,
      },
      {
        name: this.intl.t('payments.invoice.table.header.invoice_date'),
        valuePath: COLUMNS.INVOICE_DATE,
        displayValuePath: COLUMNS.INVOICE_DATE_FORMATTED,
        textAlign: 'left' as const,
        minWidth: 100,
        maxWidth: 120,
      },
      {
        name: this.intl.t('payments.invoice.table.header.due_date'),
        valuePath: COLUMNS.DUE_DATE,
        displayValuePath: COLUMNS.DUE_DATE_FORMATTED,
        textAlign: 'left' as const,
        minWidth: 100,
        maxWidth: 120,
      },
      {
        name: this.intl.t('payments.invoice.table.header.invoice_total'),
        valuePath: COLUMNS.INVOICE_TOTAL,
        textAlign: 'right' as const,
        isSortable: false,
        minWidth: 100,
        maxWidth: 150,
      },
      {
        name: '',
        valuePath: '',
      },
      {
        name: '',
        valuePath: COLUMNS.INVOICE,
        tdComponent: TransactionCellsTdButton,
        isSortable: false,
        width: 110,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    return this.args.transactions.map((transaction) => {
      return {
        [COLUMNS.INVOICE_NUMBER]: transaction.transactionNumber,
        [COLUMNS.INVOICE_DATE]:
          transaction.transactionDate?.format('YYYY-MM-DD'),
        [COLUMNS.INVOICE_DATE_FORMATTED]: transaction.transactionDateFormatted,
        [COLUMNS.DUE_DATE]: transaction.dueDate?.format('YYYY-MM-DD'),
        [COLUMNS.DUE_DATE_FORMATTED]: transaction.dueDateFormatted,
        [COLUMNS.INVOICE_TOTAL]: transaction.invoiceTotal,
        [COLUMNS.INVOICE]: transaction.pdfUrl
          ? {
              value: transaction.pdfUrl,
              label: this.intl.t(
                TRANSACTION_TYPE_TRANSLATION_KEYS[transaction.transactionType],
              ),
              iconBefore: 'download',
              buttonType: 'secondary',
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              onClick: () => {
                this.downloadPDF(transaction.pdfUrl);
              },
            }
          : null,
      };
    });
  }

  @action
  downloadPDF(downloadUrl: string | null): void {
    if (!downloadUrl) {
      return;
    }

    const url = formatUrl(downloadUrl, {}, getHostDomain());
    openUrl(url);
  }

  <template>
    <div class={{styles.invoices-table}}>
      <PrismTable
        @checkboxSelectionMode="multiple"
        @columns={{this.columns}}
        @enableAutoSortsUpdate={{true}}
        @initialSorts={{this.initialSorts}}
        @onRowSelect={{@setRowSelection}}
        @removeTableBorder={{true}}
        @rowSelection={{@rowSelection}}
        @rows={{this.rows}}
        @widthConstraint="gte-container-slack"
      />
    </div>
  </template>
}
